"use client";
import { useQuery } from "react-query";
import useCorpusApiService from "@/services/corpus-service";
import useChatApiService from "@/services/chat.service";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { StatsSection } from "./stats-section";
import { CorpusTable } from "./corpus-table";
import { FilterBar } from "../filter/filter-bar";
import AdminHeader from "../header/admin-header";
import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import { PaginationDefaults } from "@/constants/corpus.constants";
import { CORPUS_FILTER_CONFIG } from "@/constants/corpus-filter.constants";
import { CorpusItem } from "@/interfaces/corpus.interface";
import { PaginationInfo } from "@/interfaces/common.interface";
import { FilterCondition } from "@/interfaces/filter.interface";
import { PresignedUrl } from "@/interfaces/chat.interface";
import { BASE_VIEW_URL } from "@/constants/chat.constant";
import { RotateCcw, Loader2 } from "lucide-react";
import { FilterOperator } from "@/constants/filter.constant";
import toast from "react-hot-toast";

/**
 * CorpusManagerWrapper component is the main wrapper for the corpus management page.
 * Manages corpus statistics, filtering, pagination, sorting, and document viewing.
 * Handles CSV export and corpus retry operations. Integrates FilterBar and CorpusTable.
 * 
 * @component
 * @returns {JSX.Element} Corpus manager page with stats, filters, and table
 * 
 * @example
 * ```tsx
 * <CorpusManagerWrapper />
 * ```
 */
export const CorpusManagerWrapper = () => {
    const { getCorpusStats, getCorpuses, exportCorpuses, triggerCorpusRetry } = useCorpusApiService();
    const { getPresignedUrl } = useChatApiService();
    
    const {
        data: stats,
        isLoading: statsLoading,
        isFetching: statsFetching,
        error: statsError,
        refetch: refetchStats,
    } = useQuery(["corpusStats"], () => getCorpusStats(), {
        enabled: true,
        refetchOnMount: true,
    });

    const [page, setPage] = useState<number>(PaginationDefaults.PAGE);
    const [pageSize, setPageSize] = useState<number>(PaginationDefaults.PAGE_SIZE);
    const [sortBy, setSortBy] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
    const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);
    const [pendingFilter, setPendingFilter] = useState<Partial<FilterCondition> | null>(null);
    const prevFilterOpenRef = useRef<boolean>(false);
    const [retryingItems, setRetryingItems] = useState<Set<number>>(new Set());

    const mapOperatorToBackend = useCallback((operator: string): string => {
        const operatorMap: Record<string, string> = {
            [FilterOperator.EQUALS]: "eq",
            [FilterOperator.NOT_EQUALS]: "neq",
            [FilterOperator.IN]: "in",
            [FilterOperator.NOT_IN]: "not_in",
        };
        return operatorMap[operator] || operator;
    }, []);

    const mapStatusDisplayToBackend = useCallback((displayValue: string): string => {
        const statusMap: Record<string, string> = {
            "Success": "SUCCESS",
            "Failed": "FAILED",
            "In Progress": "INPROGRESS",
        };
        return statusMap[displayValue] || displayValue;
    }, []);

    const mapCorpusTypeDisplayToBackend = useCallback((displayValue: string): string => {
        const corpusTypeMap: Record<string, string> = {
            "Policy": "policy",
        };
        return corpusTypeMap[displayValue] || displayValue.toLowerCase();
    }, []);

    const convertConditionToBackendFormat = useCallback((condition: FilterCondition | Partial<FilterCondition>, filters: Record<string, any>) => {
        if (!condition.field || !condition.value || !condition.value.trim()) return;
        
        const fieldConfig = CORPUS_FILTER_CONFIG.fields.find((f) => f.id === condition.field);
        if (!fieldConfig) return;
        
        const backendKey = fieldConfig.backendKey;
        const backendOperator = mapOperatorToBackend(condition.operator || FilterOperator.EQUALS);

        const mapValueToBackend = (value: string): string => {
            if (condition.field === "ingestionStatus") {
                return mapStatusDisplayToBackend(value);
            }
            if (condition.field === "corpusName") {
                return mapCorpusTypeDisplayToBackend(value);
            }
            return value;
        };

        filters[backendKey] = {
            value: mapValueToBackend(condition.value),
            operator: backendOperator,
        };
    }, [mapOperatorToBackend, mapStatusDisplayToBackend, mapCorpusTypeDisplayToBackend]);

    const backendFilters = useMemo(() => {
        const filters: Record<string, any> = {};
        
        filterConditions.forEach((condition) => {
            convertConditionToBackendFormat(condition, filters);
        });
        
        if (pendingFilter && pendingFilter.field && pendingFilter.operator && pendingFilter.value && pendingFilter.value.trim()) {
            const pendingCondition: FilterCondition = {
                id: pendingFilter.id || `pending-${Date.now()}`,
                field: pendingFilter.field,
                operator: pendingFilter.operator,
                value: pendingFilter.value,
            };
            convertConditionToBackendFormat(pendingCondition, filters);
        }
        
        return Object.keys(filters).length > 0 ? filters : undefined;
    }, [filterConditions, pendingFilter, convertConditionToBackendFormat]);

    const {
        data: corpusResponse,
        isLoading: corpusLoading,
        isFetching: corpusFetching,
        error: corpusError,
        refetch: refetchCorpuses,
    } = useQuery(
        ["corpuses", page, pageSize, sortBy, sortOrder, backendFilters],
        () =>
            getCorpuses({
                page,
                page_size: pageSize,
                sortBy: sortBy || undefined,
                sortOrder: sortOrder || undefined,
                filters: backendFilters,
            }),
        {
            enabled: true,
            refetchOnMount: true,
        }
    );

    const corpusData: any = Array.isArray(corpusResponse?.data) 
        ? corpusResponse.data 
        : [];
    const pagination: PaginationInfo | undefined = corpusResponse?.pagination;

    useEffect(() => {
        refetchCorpuses();
    }, [filterConditions, refetchCorpuses]);

    useEffect(() => {
        if (prevFilterOpenRef.current && !isFilterOpen) {
            setFilterConditions([]);
            setPendingFilter(null);
            setPage(1);
        }
        prevFilterOpenRef.current = isFilterOpen;
    }, [isFilterOpen]);

    const handleRefresh = useCallback(() => {
        refetchStats();
        refetchCorpuses();
    }, [refetchStats, refetchCorpuses]);

    const isRefreshing = statsFetching || corpusFetching;

    const handleSortChange = useCallback((key: string, order: "asc" | "desc" | null) => {
        setSortBy(key);
        setSortOrder(order);
        setPage(1);
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    }, []);

    const handleViewDocument = useCallback(async (item: CorpusItem) => {
        if (!item.filePath) {
            console.error("No file path available for document");
            return;
        }
        try {
            const rawPath = item.filePath.trim();

            const payload: PresignedUrl = {
                blob_path: rawPath,
                expiry_hours: 2,
                permission: 'r'
            };
            
            const { presigned_url } = await getPresignedUrl("policy", payload);
            
            if (!presigned_url) {
                throw new Error('No presigned URL returned');
            }

            const viewerUrl = `${BASE_VIEW_URL}?src=${encodeURIComponent(presigned_url)}`;
            window.open(viewerUrl, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error("Error fetching presigned URL:", error);
        }
    }, [getPresignedUrl]);

    const handleRetry = useCallback(async (item: CorpusItem) => {
        if (!item.filePath) {
            toast.error("File path is missing. Cannot retry this file.");
            return;
        }

        const itemId = item.id;
        if (retryingItems.has(itemId)) return;
        
        setRetryingItems((prev) => new Set(prev).add(itemId));
        try {
            const response = await triggerCorpusRetry(item.id);
            
            if (response?.message) {
                toast.success(response.message || "Retry triggered successfully. The file is being reprocessed.");
            } else {
                toast.success("Retry triggered successfully. The file is being reprocessed.");
            }

            await Promise.all([
                refetchCorpuses(),
                refetchStats()
            ]);
        } catch (error: any) {
            console.error("Error triggering corpus retry:", error);
            const errorMessage = error?.error || error?.message || "Failed to trigger retry. Please try again.";
            toast.error(errorMessage);
        } finally {
            setRetryingItems((prev) => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        }
    }, [triggerCorpusRetry, refetchCorpuses, refetchStats, retryingItems]);

    const handleFilterToggle = useCallback(() => {
        setIsFilterOpen((prev) => !prev);
    }, []);

    const handleConditionChange = useCallback((condition: FilterCondition) => {
        setFilterConditions((prev) => {
            const existingIndex = prev.findIndex((c) => c.id === condition.id);
            
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = condition;
                return updated;
            } else {
                return [...prev, condition];
            }
        });
        setPage(1);
    }, []);

    const handleConditionRemove = useCallback((conditionId: string) => {
        setFilterConditions((prev) => {
            const afterRemoval = prev.filter((c) => c.id !== conditionId);
            return afterRemoval;
        });
        setPage(1);
    }, []);

    const handleConditionAdd = useCallback(() => {
        setPendingFilter(null);
        setPage(1);
    }, []);

    const handlePendingFilterChange = useCallback((pending: Partial<FilterCondition> | null) => {
        setPendingFilter(pending);
        setPage(1); 
    }, []);

    const handleDownloadCSV = useCallback(async () => {
        try {
            const blob = await exportCorpuses(backendFilters || undefined);
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            link.download = `corpus_export_${timestamp}.csv`;
            
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading CSV:", error);
            alert("Failed to download CSV. Please try again.");
        }
    }, [backendFilters, exportCorpuses]);

    return (
        <div className="h-full flex flex-col bg-background text-text-500 px-4">
            <div className="flex justify-between pb-6">
                <AdminHeader header={ADMIN_PANEL_HEADER.CORPUS_MANAGER} />
                <div className="flex justify-end">
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="font-medium text-[#005587] text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed  cursor-pointer"
                    >
                        {isRefreshing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RotateCcw className="h-4 w-4" />
                        )}
                        <span className="cursor-pointer">Refresh Data</span>
                    </button>
                </div>
            </div>
            
            <StatsSection stats={stats} />
            
            {/* Filter Bar Section */}
            <div className="mt-6">
                <FilterBar
                    isFilterOpen={isFilterOpen}
                    onFilterToggle={handleFilterToggle}
                    onFilterClose={handleFilterToggle}
                    conditions={filterConditions}
                    config={CORPUS_FILTER_CONFIG}
                    onConditionChange={handleConditionChange}
                    onConditionRemove={handleConditionRemove}
                    onConditionAdd={handleConditionAdd}
                    onPendingFilterChange={handlePendingFilterChange}
                    onDownloadCSV={handleDownloadCSV}
                />
            </div>
            
            {/* Table Section */}
            <div className="my-6 flex-1">
                <CorpusTable
                    data={corpusData}
                    pagination={pagination}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={corpusLoading}
                    onViewDocument={handleViewDocument}
                    onRetry={handleRetry}
                    retryingItems={retryingItems}
                />
            </div>
        </div>
    );
};